import {
  autoinject,
  bindable,
  child,
  children,
  ComponentAttached,
  ComponentDetached,
  Container,
  customElement,
  inlineView,
  TaskQueue,
  ViewResources
} from "aurelia-framework";
import { createGrid, ColDef, ComponentUtil, Grid, GridApi, GridOptions, GridParams, ValueFormatterParams, _combineAttributesAndGridOptions, _processOnChange } from "ag-grid-community";
// import { AureliaFrameworkFactory } from "./aureliaFrameworkFactory";
import { AgGridColumn } from "./ag-grid-column";
import { generateBindables } from "./ag-utils";
import { AgDateTemplate } from './ag-template';
import { AureliaFrameworkComponentWrapper } from "./aurelia-framework-component-wrapper";
import { AureliaFrameworkOverrides } from './aurelia-framework-overrides';
import { AgDateEditor } from '../../agElements/editors/agDateEditor';
import { DateConverterValueConverter } from '../../resources/value-converters/date-converter';
import { AgNumericEditor } from '../../agElements/editors/agNumericEditor';
import { AgPriceEditor } from '../../agElements/editors/agPriceEditor';
import { NumericFormatterValueConverter } from '@dynamik/common';
import { ActionType } from '@dynamik/common/clients/transaction-clients';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
// import { INTERNAL_EVENTS } from 'ag-grid-community/dist/types/core/eventTypes';

interface IPropertyChanges {
  [key: string]: any
}

@customElement('ag-grid-aurelia')
@generateBindables(ComponentUtil.ALL_PROPERTIES)
@generateBindables(ComponentUtil.PUBLIC_EVENTS as unknown as string[])
// <slot> is required for @children to work.  https://github.com/aurelia/templating/issues/451#issuecomment-254206622
@inlineView(`<template><slot></slot><grid-columns-button grid.bind="$this"></grid-columns-button></template>`)
@autoinject()
export class AgGridAurelia implements ComponentAttached, ComponentDetached {
  // not intended for user to interact with. so putting _ in so if user gets reference
  // to this object, they kind'a know it's not part of the agreed interface
  private _nativeElement: any;
  private _initialised = false;
  private _destroyed = false;

  @bindable() public gridOptions: GridOptions;
  @bindable() public context: any;

  private gridParams: GridParams;

  // making these public, so they are accessible to people using the aurelia component references
  public api: GridApi;

  @children('ag-grid-column')
  public columns: AgGridColumn[] = [];

  // @child('ag-full-width-row-template')
  // public fullWidthRowTemplate: AgFullWidthRowTemplate;

  // @child('ag-date-template')
  // public dateTemplate: AgDateTemplate;

  @child('ag-loading-overlay-template')
  public loadingOverlayTemplate: AgDateTemplate;

  constructor(element: Element, private taskQueue: TaskQueue, private container: Container, private viewResources: ViewResources, private aureliaFrameworkComponentWrapper: AureliaFrameworkComponentWrapper,
    private aureliaFrameworkOverrides: AureliaFrameworkOverrides, private dateValueConverter: DateConverterValueConverter, private numericFormatter: NumericFormatterValueConverter) {
    this._nativeElement = element;
    // create all the events generically. this is done generically so that
    // if the list of grid events change, we don't need to change this code.
    (ComponentUtil.PUBLIC_EVENTS as unknown as string[]).forEach((eventName) => {
      //create an empty event
      (<any>this)[eventName] = () => {
      };
    });
  }

  attached() {
    // initialize the grid in the queue
    // because of bug in @children
    // https://github.com/aurelia/templating/issues/403
    this.taskQueue.queueTask(this.initGrid.bind(this));
  }

  initGrid(): void {
    this._initialised = false;
    this._destroyed = false;

    // this.auFrameworkFactory.setContainer(this.container);
    // this.auFrameworkFactory.setViewResources(this.viewResources);

    this.aureliaFrameworkComponentWrapper.setContainer(this.container);
    this.aureliaFrameworkComponentWrapper.setViewResources(this.viewResources);

    this.gridOptions = _combineAttributesAndGridOptions(this.gridOptions, this);

    // Add case-insensitive default comparator so that e.g. "BLUENORD" and "Bank USD" sort correctly.
    // Only applies to string values; numbers/dates fall through to default AG Grid comparison.
    this.gridOptions.defaultColDef = {
      ...this.gridOptions.defaultColDef,
      comparator: this.gridOptions.defaultColDef?.comparator ?? ((valueA, valueB) => {
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return -1;
        if (valueB == null) return 1;
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB, undefined, { sensitivity: 'base' });
        }
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      })
    };

    // date columns hold ISO strings (with time) from the generated clients; without this the grid
    // infers them as plain text and date filtering breaks. The parser must also accept dd.MM.yyyy:
    // the advanced filter round-trips values through the column's valueFormatter before re-parsing.
    this.gridOptions.dataTypeDefinitions = {
      ...this.gridOptions.dataTypeDefinitions,
      dateString: {
        baseDataType: 'dateString',
        extendsDataType: 'dateString',
        dateParser: value => {
          if (!value) {
            return undefined;
          }
          if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
            return parse(value, 'dd.MM.yyyy', new Date());
          }
          return new Date(value);
        },
        dateFormatter: value => value ? format(value, 'yyyy-MM-dd') : undefined
      }
    };

    this.gridOptions.columnTypes = {
      ...this.gridOptions.columnTypes, ...{
        'date': {
          filter: 'agDateColumnFilter',
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellDataType: 'dateString',
          cellEditor: AgDateEditor,
          valueFormatter: x => x.value ? format(x.value, 'dd.MM.yyyy') : ''
        },
        'datetime': {
          filter: 'agDateColumnFilter',
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellDataType: 'dateString',
          cellEditor: AgDateEditor,
          valueFormatter: x => x.value ? format(x.value, 'dd.MM.yyyy HH:mm:ss') : ''
        },
        'number': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgNumericEditor
        },
        'amount': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgNumericEditor,
          valueFormatter: x => this.numericFormatter.toView(x.value, 'fourDec') as string,
          getQuickFilterText: x => this.numericQuickFilterText(x.value, 'fourDec')
        },
        'price': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgPriceEditor,
          valueFormatter: x => this.numericFormatter.toView(x.value, 'sixDec') as string,
          getQuickFilterText: x => this.numericQuickFilterText(x.value, 'sixDec')
        },
        'tenDecPrice': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgPriceEditor,
          cellEditorParams: { format: 'tenDec' },
          valueFormatter: x => this.numericFormatter.toView(x.value, 'tenDec') as string
        },
        'amountOneDec': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgNumericEditor,
          valueFormatter: x => this.numericFormatter.toView(x.value, 'oneDec') as string,
          getQuickFilterText: x => this.numericQuickFilterText(x.value, 'oneDec')
        },
        'amountTwoDec': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgNumericEditor,
          valueFormatter: x => this.numericFormatter.toView(x.value, 'twoDec') as string,
          getQuickFilterText: x => this.numericQuickFilterText(x.value, 'twoDec')
        },
        'amountFourDec': {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellEditor: AgNumericEditor,
          valueFormatter: x => this.numericFormatter.toView(x.value, 'fourDec') as string,
          getQuickFilterText: x => this.numericQuickFilterText(x.value, 'fourDec')
        },
        'actionType': {
          valueFormatter: x => ActionType[x.value] ?? '',
          filter: 'agSetColumnFilter',
          filterParams: { valueFormatter: x => ActionType[x.value] ?? '' }
        }
      } as GridOptions['columnTypes']
    };

    this.gridParams = {
      globalEventListener: this.globalEventListener.bind(this),
      frameworkOverrides: this.aureliaFrameworkOverrides,
      providedBeanInstances: {
        frameworkComponentWrapper: this.aureliaFrameworkComponentWrapper
      }
    };

    if (this.columns && this.columns.length > 0) {
      this.gridOptions.columnDefs = this.columns.map(x => this.withQuickFilterText(x.toColDef(this.aureliaFrameworkComponentWrapper)));
    }

    this.gridOptions.columnMenu = 'legacy';

    // if (this.fullWidthRowTemplate) {
    //   this.gridOptions.fullWidthCellRendererFramework =
    //     { template: this.fullWidthRowTemplate.template };
    // }

    // if (this.dateTemplate) {
    //   this.gridOptions.dateComponentFramework =
    //     { template: this.dateTemplate.template };
    // }

    if (this.loadingOverlayTemplate) {
      this.gridOptions.loadingOverlayComponent = this.aureliaFrameworkComponentWrapper.createRenderer(this.loadingOverlayTemplate.template);
    }

    this.api = createGrid(this._nativeElement, this.gridOptions, this.gridParams);
    this._initialised = true;
  }

  /**
   * Called by Aurelia whenever a bound property changes
   */
  propertyChanged(propertyName: string, newValue: any, oldValue: any) {
    // emulate an Angular2 SimpleChanges Object
    let changes: IPropertyChanges = {};
    changes[propertyName] = <any>{ currentValue: newValue, previousValue: oldValue };

    if (this._initialised) {
      _processOnChange(changes, this.api);
    }
  }

  public detached(): void {
    if (this._initialised) {
      // need to do this before the destroy, so we know not to emit any events
      // while tearing down the grid.
      this._destroyed = true;
      this.api.destroy();
    }
  }

  private globalEventListener(eventType: string, event: any): void {
    // if we are tearing down, don't emit events
    if (this._destroyed) {
      return;
    }
    // generically emit the event
    (<any>this)[eventType]?.(event);
    // if ((INTERNAL_EVENTS as unknown as string[]).includes(eventType)) {
    //   return;
    // }
  }

  // quick search matches what the user sees: columns with a formatter (e.g. enum names) are
  // searchable by their displayed text as well as the raw value
  private withQuickFilterText(colDef: ColDef): ColDef {
    const formatter = colDef.valueFormatter;
    if (typeof formatter === 'function' && !colDef.getQuickFilterText) {
      colDef.getQuickFilterText = params => `${params.value ?? ''} ${formatter(params as unknown as ValueFormatterParams) ?? ''}`;
    }
    return colDef;
  }

  // quick search matches what the user sees (comma decimals, with or without thousand separators)
  // as well as the raw value
  private numericQuickFilterText(value: number | null | undefined, format: string): string {
    if (value == null) {
      return '';
    }
    const formatted = String(this.numericFormatter.toView(value, format) ?? '');
    return `${value} ${formatted} ${formatted.replace(/[\s ]/g, '')}`;
  }

  async updateColumns() {
    await Promise.delay(100);
    if (this._initialised) {
      this.api.setGridOption('columnDefs', this.columns.map(x => this.withQuickFilterText(x.toColDef(this.aureliaFrameworkComponentWrapper))));
    }
  }

}
